import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Zap, Sun, Gauge, Clock, PlugZap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const kw = (w) => (w == null ? "—" : `${(w / 1000).toFixed(1)} kW`);
const hhmm = (ts) => { try { return new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const dur = (s) => { if (s == null) return "—"; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return `${h}h ${m}m`; };

const MODES = [
  { id: "off", label: "Off" },
  { id: "solo_sole", label: "Solo Sole" },
  { id: "boost", label: "Boost" },
];

export function StationDetailDialog({ station, onClose }) {
  const [det, setDet] = useState(null);
  const [meter, setMeter] = useState(null);
  const [plan, setPlan] = useState(null);
  const [mode, setMode] = useState("off");
  const [buffer, setBuffer] = useState(300);
  const [autoApply, setAutoApply] = useState(false);
  const [saving, setSaving] = useState(false);
  const [capKw, setCapKw] = useState("");
  const [capTol, setCapTol] = useState(10);
  const [capRis, setCapRis] = useState(1.5);
  const [capSugg, setCapSugg] = useState(null);
  const [capSaving, setCapSaving] = useState(false);

  const loadAll = async () => {
    try {
      const d = await api.get(`/ev/stations/${station.id}`);
      setDet(d.data);
      if (d.data?.plant_ref) loadCap(d.data.plant_ref);
      const ss = await api.get(`/ev/stations/${station.id}/sessions`, { params: { limit: 1 } });
      const sess = (ss.data?.sessions || [])[0];
      if (sess?.id) {
        const m = await api.get(`/ev/sessions/${sess.id}/meter`);
        setMeter(m.data);
      } else { setMeter({ session: null, points: [] }); }
    } catch { /* ignore */ }
    try {
      const pol = await api.get(`/ev/stations/${station.id}/policy`);
      setMode(pol.data?.mode || "off"); setBuffer(pol.data?.buffer_w ?? 300); setAutoApply(!!pol.data?.auto_apply);
    } catch { /* ignore */ }
    loadPlan();
  };
  const loadPlan = async () => {
    try { const p = await api.get(`/ev/stations/${station.id}/plan`); setPlan(p.data); } catch { /* ignore */ }
  };
  const loadCap = async (plantRef) => {
    try {
      const mc = await api.get(`/ev/plants/${plantRef}/meter-cap`);
      setCapKw(mc.data?.potenza_kw ?? "");
      setCapTol(mc.data?.tolleranza_pct ?? 10);
      setCapRis(mc.data?.riserva_kw ?? 1.5);
      setCapSugg(mc.data?.suggerito_da_bolletta ?? null);
    } catch { /* ignore */ }
  };
  const saveCap = async () => {
    setCapSaving(true);
    try {
      await api.put(`/ev/plants/${det.plant_ref}/meter-cap`, {
        potenza_kw: capKw === "" ? null : Number(capKw),
        tolleranza_pct: Number(capTol), riserva_kw: Number(capRis),
      });
      toast.success(capKw === "" ? "Tetto contatore rimosso" : "Tetto contatore salvato");
      loadPlan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setCapSaving(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const savePolicy = async () => {
    setSaving(true);
    try {
      await api.put(`/ev/stations/${station.id}/policy`, { mode, buffer_w: Number(buffer) || 0, auto_apply: autoApply });
      toast.success("Modalità salvata");
      loadPlan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const points = (meter?.points || []).map((p) => ({ t: hhmm(p.ts), kw: p.power_w != null ? +(p.power_w / 1000).toFixed(2) : null, limite: p.limit_w != null ? +(p.limit_w / 1000).toFixed(2) : null }));
  const sess = meter?.session;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="ev-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PlugZap className="h-5 w-5 text-primary" />{station.nome || station.ocpp_id}</DialogTitle></DialogHeader>
        {!det ? (
          <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Caricamento…</div>
        ) : (
          <div className="space-y-5">
            {/* Connettori */}
            <div data-testid="ev-detail-connettori">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Connettori</div>
              <div className="space-y-1">
                {(det.connettori || []).map((c) => (
                  <div key={c.connector_id} className="flex items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                    <span className="font-mono">#{c.connector_id}</span>
                    <span className="flex-1">{c.stato}{c.error_code && c.error_code !== "NoError" ? ` · ${c.error_code}` : ""}</span>
                    <span className="font-semibold">{kw(c.potenza_w)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sessione */}
            <div data-testid="ev-detail-sessione">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Ultima sessione di ricarica</div>
              {sess ? (
                <div className="flex flex-wrap gap-x-5 gap-y-1 rounded-lg bg-muted p-3 text-sm">
                  <span className="inline-flex items-center gap-1"><Gauge className="h-4 w-4" /><b>{sess.energy_kwh != null ? `${sess.energy_kwh} kWh` : "—"}</b></span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{dur(sess.duration_s)}</span>
                  <span>Inizio {hhmm(sess.start_time)}</span>
                  {sess.stop_time && <span>Fine {hhmm(sess.stop_time)}</span>}
                  <span className="rounded-full bg-background px-2 py-0.5 text-xs">{sess.status}</span>
                </div>
              ) : <p className="text-sm text-muted-foreground">Nessuna sessione registrata.</p>}
            </div>

            {/* Grafico */}
            {points.length > 0 && (
              <div data-testid="ev-detail-chart">
                <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Potenza di ricarica (kW)</div>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={30} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="kw" name="Potenza" stroke="#F5B70A" strokeWidth={2} dot={false} />
                      {points.some((p) => p.limite != null) && <Line type="stepAfter" dataKey="limite" name="Limite" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Smart charging */}
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="ev-detail-smart">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold"><Sun className="h-4 w-4 text-primary" />Ricarica intelligente</div>
              <div className="flex flex-wrap items-center gap-2">
                {MODES.map((m) => (
                  <Button key={m.id} size="sm" variant={mode === m.id ? "default" : "outline"} onClick={() => setMode(m.id)} data-testid={`ev-mode-${m.id}`}>{m.label}</Button>
                ))}
                {mode === "solo_sole" && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">Margine
                    <Input type="number" value={buffer} onChange={(e) => setBuffer(e.target.value)} className="h-8 w-20" data-testid="ev-buffer-input" />W</span>
                )}
                <Button size="sm" onClick={savePolicy} disabled={saving} data-testid="ev-policy-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
              </div>
              {mode !== "off" && (
                <label className="mt-2 flex items-center gap-2 text-sm" data-testid="ev-autoapply-row">
                  <Switch checked={autoApply} onCheckedChange={setAutoApply} data-testid="ev-autoapply-switch" />
                  <span>Regolazione automatica della ricarica</span>
                </label>
              )}
              {plan && (
                <div className="mt-3 space-y-1 text-sm" data-testid="ev-plan">
                  <div className="flex flex-wrap gap-x-5 gap-y-1">
                    <span className="inline-flex items-center gap-1"><Sun className="h-4 w-4 text-amber-500" />Produzione: <b>{kw(plan.production_w)}</b></span>
                    <span>Consumi: <b>{plan.consumo_disponibile ? kw(plan.consumption_w) : "n/d"}</b></span>
                    <span className="inline-flex items-center gap-1"><Zap className="h-4 w-4 text-primary" />Consigliato: <b>{plan.target_w == null ? "—" : kw(plan.target_w)}</b></span>
                  </div>
                  <p className="font-medium">{plan.azione}{plan.plant_nome ? ` · impianto ${plan.plant_nome}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{plan.nota}</p>
                </div>
              )}
            </div>

            {det.plant_ref && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4" data-testid="ev-meter-cap">
                <div className="mb-1 flex items-center gap-2 text-sm font-bold"><Gauge className="h-4 w-4 text-sky-600" />Tetto contatore (impianto)</div>
                <p className="mb-3 text-xs text-muted-foreground">Potenza contrattuale del contatore{det.plant_nome ? ` dell'impianto ${det.plant_nome}` : ""}: la ricarica viene limitata per non far scattare il contatore. Vale per tutte le colonnine collegate a questo impianto. Lascia vuota la potenza per disattivare il limite.</p>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">Potenza contratto (kW)
                    <Input type="number" step="0.5" value={capKw} onChange={(e) => setCapKw(e.target.value)} placeholder={capSugg ? String(capSugg) : "es. 6"} className="h-8 w-28" data-testid="ev-cap-kw" />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">Tolleranza (%)
                    <Input type="number" value={capTol} onChange={(e) => setCapTol(e.target.value)} className="h-8 w-20" data-testid="ev-cap-tol" />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">Riserva casa (kW)
                    <Input type="number" step="0.5" value={capRis} onChange={(e) => setCapRis(e.target.value)} className="h-8 w-24" data-testid="ev-cap-ris" />
                  </label>
                  <Button size="sm" onClick={saveCap} disabled={capSaving} data-testid="ev-cap-save">{capSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
                </div>
                {capSugg && !capKw && <p className="mt-2 text-xs text-sky-700" data-testid="ev-cap-suggerito">Dalla bolletta risulta {capSugg} kW — inseriscila e salva per usarla.</p>}
                {plan?.meter_cap_w != null && <p className="mt-2 text-xs text-muted-foreground" data-testid="ev-cap-attuale">Limite di ricarica attuale dal contatore: <b>{(plan.meter_cap_w / 1000).toFixed(1)} kW</b></p>}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
